import React from 'react';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl';
import { css } from '@compiled/react';

import UFOSegment from '@atlaskit/react-ufo/segment';
import { token } from '@atlaskit/tokens';

import { HOME_STARRED_EXPERIENCE } from '@confluence/experience-tracker/entry-points/ExperienceName.ts';
import { PageCards } from '@confluence/page-card/entry-points/PageCards.ts';
import { PageSegmentLoadEnd } from '@confluence/browser-metrics/entry-points/PageSegmentLoadEnd.ts';
import { PageSegmentLoadStart } from '@confluence/browser-metrics/entry-points/PageSegmentLoadStart.ts';
import { DocumentContentTitle } from '@confluence/document-title/entry-points/DocumentContentTitle.ts';
import { mergeLiveDocI18n } from '@confluence/live-pages-utils/entry-points/mergeLiveDocI18n.ts';

import { untitled, HomeViewFilters } from '../homeViewsQueryHelpers.ts';
import { Options, useOptions } from '../Options/index.ts';
import { LoadingAndPagination } from '../LoadingAndPagination.tsx';
import { ExperienceTracking } from '../ExperienceTracking.tsx';
import StarredEmptyStateLight from '../images/starred/StarredEmptyStateLight.svg';
import StarredEmptyStateDark from '../images/starred/StarredEmptyStateDark.svg';
import { HOME_STARRED_VIEW_SEGMENT } from '../../../perf.config.ts';

import { useStarredData } from './useStarredData.ts';

const i18nBase = defineMessages({
	title: {
		id: 'home.starred-home-view.title',
		defaultMessage: 'Starred',
		description: 'The title for Home starred pages view',
	},
	noStarredResults: {
		id: 'home.views.starred.no-results',
		// TODO: replace straight quotes with curly quotes (see go/curlyquotes)

		defaultMessage: 'We can’t find any starred content that matches.',
		description: 'Header shown when all Starred pages have been filtered out',
	},
	noStarredResultsSubtext: {
		id: 'home.views.starred.no-results-subtext',
		defaultMessage: '<a>Search all pages instead</a>.',
		description: 'message suggesting navigation to advanced search',
	},
	emptyHeader: {
		id: 'home.views.starred.empty.header',
		defaultMessage: 'You haven’t starred any pages yet',
		description:
			'Descriptive header that appears on the Starred Pages Home view when the user has no starred Pages.',
	},
	emptyBody: {
		id: 'home.views.starred.empty.body',
		defaultMessage:
			'Get back to pages that are important to you by marking them. You won’t get any notifications about pages you star.',
		description:
			'Descriptive body text that appears on the Starred Pages Home view when the user has no starred Pages.',
	},
});

const i18nLiveDocs = defineMessages({
	noStarredResultsSubtext: {
		id: 'home.views.starred.no-results-subtext.livedocs',
		defaultMessage: '<a>Search all pages and live docs instead</a>.',
		description: 'Updated version of home.views.starred.no-results-subtext',
	},

	emptyHeader: {
		id: 'home.views.starred.empty.header.livedocs',
		defaultMessage: 'You haven’t starred any pages or live docs yet',
		description: 'Updated version of home.views.starred.empty.header',
	},

	emptyBody: {
		id: 'home.views.starred.empty.body.livedocs',
		defaultMessage:
			'Get back to pages and live docs that are important to you by marking them. You won’t get any notifications about content you star.',
		description: 'Updated version of home.views.starred.empty.body',
	},
});

const i18n = mergeLiveDocI18n(i18nBase, i18nLiveDocs);

const starredContainerStyles = css({
	display: 'flex',
	flexDirection: 'column',
	width: '100%',
});

const starredHeaderStyles = css({
	font: token('font.heading.large'),
	marginBottom: token('space.200'),
});

const pageCardsContainerStyles = css({
	position: 'relative',
});

const starredEmptyStateImage = {
	src: StarredEmptyStateLight,
	srcDark: StarredEmptyStateDark,
};

export const Starred = () => {
	const intl = useIntl();
	const optionsProps = useOptions();

	const { pageCardData, loading, loadMore, hasNextPage, error } = useStarredData({
		filterText: optionsProps.filterText,
		translatedUntitled: intl.formatMessage(untitled),
	});

	return (
		<UFOSegment name="all-starred-page">
			<div css={starredContainerStyles} data-testid="home_starred">
				<PageSegmentLoadStart metric={HOME_STARRED_VIEW_SEGMENT} />
				<DocumentContentTitle titlePrefix={intl.formatMessage(i18n.title)} />
				{/* eslint-disable-next-line @atlaskit/design-system/use-heading */}
				<h1 css={starredHeaderStyles}>
					<FormattedMessage {...i18n.title} />
				</h1>
				<Options {...optionsProps} />
				<div css={pageCardsContainerStyles}>
					{!optionsProps.loading && (
						<PageCards
							appearance={optionsProps.appearance}
							nodes={pageCardData}
							analyticsData={{ source: 'starredScreen' }}
						/>
					)}
					<LoadingAndPagination
						pageCardDataLength={pageCardData.length}
						loading={loading}
						hasNextPage={hasNextPage}
						loadMore={loadMore}
						error={error}
						noResultsMessage={i18n.noStarredResults}
						noResultsSubtext={i18n.noStarredResultsSubtext}
						emptyMessageHeader={i18n.emptyHeader}
						emptyMessageBody={i18n.emptyBody}
						emptyMessageImage={starredEmptyStateImage}
						filterText={optionsProps.filterText}
						source="starredScreen"
						filter={HomeViewFilters.STARRED}
					/>
					<ExperienceTracking
						experienceName={HOME_STARRED_EXPERIENCE}
						loading={loading}
						error={error}
					/>
					{!loading && <PageSegmentLoadEnd metric={HOME_STARRED_VIEW_SEGMENT} />}
				</div>
			</div>
		</UFOSegment>
	);
};
